import { node, icon } from "~/util/dom";
import Tooltip, { ETooltipFlow } from "~/ui/tooltip";
import { getEmo1Pos, getEmo1Neg, getEmo2Pos, getEmo2Neg } from "./emojis-list";
import { TRatingType, IStructuredItem, TStructuredData } from "~/review/interfaces";
import { POS_SMILEY_VAL, NEG_SMILEY_VAL, UNSET_SMILEY_VAL } from "~/review/const";
import { _ } from "~/i18n";

function roundTo(value: number, roundDec: number): number {
	const floorVal = Math.floor(value / roundDec);
	const ceilVal = Math.ceil(value / roundDec);

	if (Math.abs((floorVal * roundDec) - value) < Math.abs((ceilVal * roundDec) - value)) {
		return floorVal * roundDec;
	}

	return ceilVal * roundDec;
}

/*
 * PA - pocet atributu
 * SH - přednastavené střední hodnoty/mediánu hodnocení SH (viz administrace)
 * SN - pocet nastavenych smajliku
 * PN - pocet nastavenych zamracku
 *
 * pocet hvezd = SH + PN*((5-SH)/PA) - SN*((SH-1)/PA) zaokrouhleno na 0.5
 */
function calcStars(PA: number, SH: number, SN: number, PN: number): number {
	return SH + (SN * ((5 - SH) / PA)) - (PN * ((SH - 1) / PA));
}

export type VARIANT = "new" | "edit";

function createItem(variant: VARIANT, config: IStructuredItem, onClick: (rating: TRatingType) => void): HTMLDivElement {
	let posEmoji = null;
	let negEmoji = null;
	let rating = config.rating;
	const item = node("div", { className: "review-emojis-item" });
	const nameCont = node("span", { className: "review-emojis-itemNameCont" });
	const name = node("span", { className: "review-emojis-itemName", textContent: config.name });

	function btnClick(negative?: boolean) {
		if ((negative && rating === NEG_SMILEY_VAL) || (!negative && rating === POS_SMILEY_VAL)) {
			rating = UNSET_SMILEY_VAL;
			posEmoji.setActive(false);
			negEmoji.setActive(false);
			onClick(rating);

			return;
		}

		if (negative) {
			rating = NEG_SMILEY_VAL;
			posEmoji.setActive(false);
			negEmoji.setActive(true);
			onClick(rating);
		} else {
			rating = POS_SMILEY_VAL;
			posEmoji.setActive(true);
			negEmoji.setActive(false);
			onClick(rating);
		}
	}

	nameCont.append(
		name,
	);
	posEmoji = (variant === "new" ? getEmo1Pos : getEmo2Pos)({
		active: rating === POS_SMILEY_VAL,
		onClick: () => btnClick(),
	});
	negEmoji = (variant === "new" ? getEmo1Neg : getEmo2Neg)({
		active: rating === NEG_SMILEY_VAL,
		onClick: () => btnClick(true),
	});

	item.append(
		nameCont,
		posEmoji.container,
		negEmoji.container,
	);

	return item;
}

function createTooltipLine(config: Array<IStructuredItem>): HTMLDivElement {
	const container = node("div", { className: "review-emojis-tooltip-line" });

	if (config.filter(item => item.label).length > 0) {
		const trigger = node("div", { className: "review-emojis-tooltipTrigger" });
		const text = node("span", { textContent: _("review.tooltipMoreChoices") });
		const tooltipList = node("ul", { className: "review-emojis-tooltip-list" });

		config.forEach(item => {
			const tooltipText = item.label;

			if (tooltipText) {
				const tooltipItem = node("li", { className: "review-emojis-tooltip-listItem", textContent: tooltipText });

				tooltipList.append(tooltipItem);
			}
		});
		trigger.append(
			icon("square-info"),
			text,
		);
		container.append(trigger);

		/* eslint-disable no-new */
		new Tooltip({
			anchor: trigger,
			trigger,
			triggerEventType: "pointerenter",
			flow: ETooltipFlow.START,
			offset: [0, 0, -2, 0],
			content: tooltipList,
			hideTimeout: 0,
			className: "structured-review-tooltip",
			onShow: () => {
				// analytika
				Mapy.getComponent("logger").log("attributes_infotip_impress");
			},
		});
	}

	return container;
}

interface ICreateEmojis {
	variant?: VARIANT;
	lang?: string;
	config: Array<IStructuredItem>;
	buildTooltip?: boolean;
	onUpdate?: (stars: number) => void;
	SH?: number;
}

/**
 * 2 typy smajliku - zadavani, editace
 */
export function createEmojis({
	variant = "new",
	config = [],
	buildTooltip = true,
	onUpdate = () => { /**/ },
	SH = 4,
}: ICreateEmojis): {
	container: HTMLDivElement;
	getData: () => TStructuredData;
	fromConfig: (newConfig: Array<IStructuredItem>) => void,
} {
	const container = node("div", { className: "review-emojis" });
	// promenne
	let emojiValues = {};
	let PA = 0;

	function getData(): Array<{ key: string; value: TRatingType }> {
		const output = [];

		Object.keys(emojiValues).forEach(name => {
			const value = emojiValues[name];

			if (value === null || (value >= NEG_SMILEY_VAL && value <= POS_SMILEY_VAL)) {
				output.push({
					key: name,
					value,
				});
			}
		});

		return output;
	}

	function onClick(item: IStructuredItem, rating: TRatingType) {
		emojiValues[item.key] = rating;

		const keys = Object.keys(emojiValues);
		const SN = keys.filter(dataItem => emojiValues[dataItem] === POS_SMILEY_VAL).length;
		const PN = keys.filter(dataItem => emojiValues[dataItem] === NEG_SMILEY_VAL).length;
		const stars = calcStars(PA, SH, SN, PN);

		onUpdate(roundTo(stars, 0.5));
	}

	function fromConfig(newConfig: Array<IStructuredItem>) {
		const items = node("div", { className: "review-emojis-items" });

		container.innerHTML = "";
		container.classList.remove("empty");
		emojiValues = newConfig.reduce((acc, cur) => {
			acc[cur.key] = cur.rating;

			return acc;
		}, {});
		PA = Object.keys(newConfig).length;
		container.append(items);
		newConfig.forEach(item => items.append(createItem(variant, item, state => onClick(item, state))));

		if (buildTooltip) {
			container.append(createTooltipLine(newConfig));
		}

		if (newConfig.length === 0) {
			container.classList.add("empty");
		}
	}

	// init
	fromConfig(config);

	return {
		container,
		getData,
		fromConfig,
	};
}
